import React from 'react'
import { Dropdown } from 'semantic-ui-react'
import { PropTypes } from 'prop-types'
import styled from 'styled-components'
import * as R from 'ramda'

import { CrownBadge } from 'components/ProFeatureLabel'
import { TooltipLabelWithoutIcon } from 'components/TooltipLabelWithoutIcon'
import constants from 'config/constants'
import useFeatureToggle from 'hooks/useFeatureToggle'
import useUser from 'hooks/useUser'
import usePlanFeature from 'hooks/usePlanFeature'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'

const FILE_UPLOAD_LEARN_MORE_LINK =
  'https://help.simplesat.io/en/articles/2776705-survey-questions#file-upload-questions'

const LOCKED_ITEM_POPUP_POSITION = 'right center'

const DropdownButton = styled(Dropdown)`
  display: inline-flex !important;
  flex-direction: row-reverse;
  align-items: center;
  padding-left: 41px !important;
  padding-right: 41px !important;

  &.bg-white.primary.basic {
    background-color: #fff !important;
    &:focus,
    &:active {
      background-color: #fff !important;
    }
  }
  &.ui.basic.bg-white.primary.active.button,
  &.ui.basic.bg-white.primary.button:hover {
    background-color: #fff !important;
  }
`

const ProBadge = styled.span`
  margin-left: 8px;

  .ui.label {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    height: 16px;
    padding: 0 6px;
    font-size: 11px;
    line-height: 16px;
  }
`

const LOCKED_ITEM_TEXT_COLOR = '#9b9b9b'

export const AddQuestionButton = ({ onSelect, disabled, handleOpenGeneratePopup }) => {
  const { enableFileUploadQuestion } = useFeatureToggle()
  const isFileUploadPlanEnabled = usePlanFeature('file_upload', !enableFileUploadQuestion)
  const canAddFileUpload = enableFileUploadQuestion && isFileUploadPlanEnabled === true
  const shouldUpsellFileUpload = enableFileUploadQuestion && isFileUploadPlanEnabled === false
  const saveUserActivityLog = useSaveUserActivityLog()
  const user = useUser()

  const companyId = R.path(['selected_workspace', 'id'], user)

  function logFileUploadCta(action) {
    saveUserActivityLog({
      action,
      target: `com_${companyId}`,
      statusCode: 200,
    })
  }

  return (
    <DropdownButton
      text="Add question"
      icon={<i className="icon far fa-plus" />}
      floating
      button
      className="bg-white primary basic"
      disabled={disabled}
      pointing
      data-testid="add-question-dropdown"
    >
      <Dropdown.Menu>
        <Dropdown.Item
          text="Multiple choice"
          icon={<i className="icon green fas fa-list" />}
          onClick={() => onSelect(constants.questionType.multipleChoice)}
          data-testid="add-question-multiple-choice"
        />
        <Dropdown.Item
          text="Multi-select"
          icon={<i className="icon blue fas fa-list-check" />}
          onClick={() => onSelect(constants.questionType.multi_select_choice)}
          data-testid="add-question-multi-select"
        />
        <Dropdown.Item
          text="Sliding scale"
          icon={<i className="icon yellow fas fa-square-sliders" />}
          onClick={() => onSelect(constants.questionType.slider)}
          data-testid="add-question-sliding-scale"
        />
        <Dropdown.Item
          text="Comment box"
          icon={<i className="icon purple fas fa-quote-left" />}
          onClick={() => onSelect(constants.questionType.comment)}
          data-testid="add-question-comment-box"
        />
        {canAddFileUpload && (
          <Dropdown.Item
            text="File upload"
            icon={<i className="icon violet far fa-paperclip" />}
            onClick={() => onSelect(constants.questionType.file_upload)}
            data-testid="add-question-file-upload"
          />
        )}
        {shouldUpsellFileUpload && (
          <TooltipLabelWithoutIcon
            descriptionTitle={
              <span className="flex">
                File upload <CrownBadge />
              </span>
            }
            descriptionContent="Collect images and files with survey answers. Upgrade to unlock this feature."
            learnMoreLink={FILE_UPLOAD_LEARN_MORE_LINK}
            showGotoBillingPage={true}
            position={LOCKED_ITEM_POPUP_POSITION}
            onPopupOpen={() => logFileUploadCta(ACTION_NAME.viewed_file_upload_upgrade_cta)}
            onUpgradeClick={() => logFileUploadCta(ACTION_NAME.clicked_file_upload_upgrade_cta)}
            trigger={
              <Dropdown.Item
                text={
                  <span>
                    File upload
                    <ProBadge>
                      <CrownBadge />
                    </ProBadge>
                  </span>
                }
                icon={
                  <i className="icon far fa-paperclip" style={{ color: LOCKED_ITEM_TEXT_COLOR }} />
                }
                style={{ color: LOCKED_ITEM_TEXT_COLOR }}
                data-testid="add-question-file-upload-locked"
              />
            }
          />
        )}
        <Dropdown.Item
          text="Generate with AI"
          icon={<i className="icon fas fa-robot" />}
          onClick={handleOpenGeneratePopup}
          data-testid="add-question-generate-ai"
        />
      </Dropdown.Menu>
    </DropdownButton>
  )
}

AddQuestionButton.propTypes = {
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/AddQuestionButton/index.js